import { useEffect, useRef } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { dailyEventId, dailyReward, hasDailyLogin } from '../utils/daily';
import { todayIso } from '../utils/format';
import { hashStr } from '../utils/coins';
import { DAILY_LOGIN_PHRASES } from '../utils/phrases';
import { celebrateSmall } from '../utils/confetti';

/**
 * Дейлик за вход (спека §4): первое открытие за локальный день кладёт singleton
 * daily_login в общую казну. Идемпотентен: id и (type, month, week=дата)
 * детерминированы датой — с двух устройств получится одно событие.
 * Пропуск ничего не отнимает: ни стрика, ни упоминаний невзятого.
 *
 * Инвариант зависимостей useEffect:
 *   deps = [status, viewData, addGameEvent, toast]
 *
 *   `viewData` пересоздаётся при каждом рефреше BudgetContext, из-за чего
 *   эффект перезапускается многократно. Это намеренно и безопасно — тройная
 *   защита от задвоения клейма:
 *
 *   1. `attemptedFor` ref — если уже вызывали addGameEvent для текущей даты
 *      в этой сессии, немедленный ранний выход (O(1), без сетевого запроса).
 *   2. `hasDailyLogin(viewData.game_events, today)` — если снапшот уже
 *      содержит клейм (перезагрузка страницы, другое устройство успело
 *      раньше), второй ранний выход.
 *   3. `addGameEvent` идемпотентен на уровне API: детерминированный id
 *      `dl_<date>` → constraint violation → `existing: true` → тишина.
 *
 *   Почему viewData нельзя убрать из deps или читать через ref:
 *   Полуночный переход при живом приложении: когда дата меняется, BudgetContext
 *   делает рефреш и выдаёт новый viewData. Именно это изменение зависимости
 *   триггерит эффект для нового дня — без него клейм не случится до перезагрузки.
 *   Все лишние перезапуски в течение одного дня гасятся слоем 1 (attemptedFor).
 */
export function useDailyClaim(): void {
  const { status, viewData, addGameEvent } = useBudget();
  const toast = useToast();
  const attemptedFor = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'ready' || !viewData) return;
    const today = todayIso();
    // Слой 1: сессионный дедупликатор — не трогаем сеть повторно в тот же день
    if (attemptedFor.current === today) return;
    // Слой 2: снапшот уже содержит клейм → другое устройство успело, тихо уходим
    if (hasDailyLogin(viewData.game_events ?? [], today)) {
      attemptedFor.current = today;
      return;
    }
    attemptedFor.current = today;
    const { coins, crit } = dailyReward(today);
    void addGameEvent({
      id: dailyEventId(today),
      type: 'daily_login',
      month: today.slice(0, 7),
      week: today,
      coins,
      payload: JSON.stringify({ crit }),
    }).then((r) => {
      // Слой 3: API вернул existing → другое устройство обогнало нас, тихо уходим
      if (r.existing) return;
      const phrase = DAILY_LOGIN_PHRASES[hashStr(today) % DAILY_LOGIN_PHRASES.length];
      if (crit) celebrateSmall();
      toast.reward({ title: phrase, amount: `+${coins}`, crit, sub: r.synced ? undefined : 'синх позже' });
    });
  }, [status, viewData, addGameEvent, toast]);
}
